import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ShareApiError, verify } from '../../lib/shareApi'
import { GateScreen } from './GateScreen'
import { classifyOpenFailure } from './openFlow'
import { ProfileNotice } from './ProfileNotice'
import { ProfileScreen } from './ProfileScreen'
import './ProfileScreen.css'

type Phase =
  | { kind: 'opening' }
  | { kind: 'gate' }
  | { kind: 'ready'; viewToken: string }
  | { kind: 'error'; reason: ShareApiError['reason'] }

// A live view session lasts ~15 min, so a genuine lapse re-opens at most a few
// times an hour. If getProfile keeps reporting session_expired WITHOUT the
// profile ever loading (a cache-read failure, clock skew), re-opening would loop
// and burn the verify throttle — so cap consecutive re-opens between successful
// loads. onLoaded resets the count, letting a long, healthy session re-open freely.
const MAX_REOPENS = 3

/**
 * Route container for /p/:token. Link-only shares open code-free: on mount we try
 * verify(token) with NO code. Success → the live profile. A link that still needs
 * a code fails with invalid_code → the code gate (legacy links). Any other failure
 * → an error notice. The view token lives only in memory; a session lapse re-opens
 * code-free (a coded link re-prompts) up to MAX_REOPENS. A refresh restarts the flow.
 */
export function SharePage() {
  const { token = '' } = useParams()
  const [phase, setPhase] = useState<Phase>({ kind: 'opening' })
  // Bumped to re-run the code-free open after a session lapse (getProfile 401).
  const [reload, setReload] = useState(0)
  // Fire verify() exactly once per (token, reload): the ref survives <StrictMode>'s
  // dev double-invoke, so one open mints one session (not two) and bumps the view
  // count once. The key also guards against a stale run resolving after a change.
  const startedRef = useRef<string | null>(null)
  // Consecutive re-opens since the last successful load (see MAX_REOPENS).
  const reopensRef = useRef(0)

  useEffect(() => {
    const key = `${token}:${reload}`
    if (startedRef.current === key) return
    startedRef.current = key
    setPhase({ kind: 'opening' })
    verify(token)
      .then(({ viewToken }) => {
        if (startedRef.current !== key) return
        setPhase({ kind: 'ready', viewToken })
      })
      .catch((err) => {
        if (startedRef.current !== key) return
        const reason = err instanceof ShareApiError ? err.reason : 'unknown'
        setPhase(
          classifyOpenFailure(reason) === 'gate'
            ? { kind: 'gate' }
            : { kind: 'error', reason }
        )
      })
  }, [token, reload])

  const onSessionExpired = useCallback(() => {
    if (reopensRef.current >= MAX_REOPENS) {
      setPhase({ kind: 'error', reason: 'session_expired' })
      return
    }
    reopensRef.current += 1
    setReload((n) => n + 1)
  }, [])

  const onLoaded = useCallback(() => {
    reopensRef.current = 0
  }, [])

  switch (phase.kind) {
    case 'ready':
      return (
        <ProfileScreen
          viewToken={phase.viewToken}
          onSessionExpired={onSessionExpired}
          onLoaded={onLoaded}
        />
      )
    case 'gate':
      return (
        <GateScreen
          token={token}
          onVerified={(viewToken) => setPhase({ kind: 'ready', viewToken })}
        />
      )
    case 'error':
      return <ProfileNotice reason={phase.reason} />
    default:
      return (
        <main className='profile profile--center'>
          <div className='profile__spinner' role='status'>
            <span className='visually-hidden'>Opening profile</span>
          </div>
        </main>
      )
  }
}
